import {useAuth} from "@bounce/shared/contexts";
import {LoadingCenter, updateButtonRadius, updateDynamicColors, updateFavicon} from "@bounce/shared/ui";
import {PartnerDetails, PartnerDomain} from "@bounce/shared/consumer/declarations";
import {Colors} from "@bounce/shared/ui/colors-base";
import React, {createContext, PropsWithChildren, useContext, useMemo} from "react";
import {useGetPartnerUiSettings} from "~/api/requests";

interface PartnerUiContextType {
  whiteLabelEnabled: boolean;
  partnerDetails?: PartnerDetails;
  logoUrl?: string;
  partnerCode?: string;
  partnerId?: string;
}

const PartnerUiContext = createContext<PartnerUiContextType>({whiteLabelEnabled: false});

export const usePartnerUi = () => useContext(PartnerUiContext);

export const PartnerUiProvider = ({children}: PropsWithChildren) => {
  const {partnerDomain} = useAuth();
  const whiteLabelEnabled = useMemo(
    () => !!partnerDomain && partnerDomain !== PartnerDomain.BOUNCE_AI,
    [partnerDomain]
  );
  const {
    data: partnerUiSettings,
    isLoading,
    isFetched,
  } = useGetPartnerUiSettings({partnerDomain}, {enabled: !!partnerDomain && whiteLabelEnabled});

  const partnerColors = partnerUiSettings?.ui_settings.colors;
  const partnerButtonRadius = partnerUiSettings?.ui_settings.button_radius;
  const partnerFaviconUrl = partnerUiSettings?.ui_settings.favicon_url;

  if (partnerColors) {
    updateDynamicColors(partnerColors);
  }
  if (partnerButtonRadius) {
    updateButtonRadius(partnerButtonRadius);
  }
  if (partnerFaviconUrl) {
    updateFavicon(partnerFaviconUrl);
  }

  const partnerDetails: PartnerDetails | undefined = useMemo(
    () =>
      partnerUiSettings
        ? {
            partnerName: partnerUiSettings.name,
            address: partnerUiSettings.address,
            phoneNumber: partnerUiSettings.ui_settings.phone_number,
            links: partnerUiSettings.ui_settings.links,
            appUrl: partnerUiSettings.app_url,
            email: partnerUiSettings.ui_settings.email,
          }
        : undefined,
    [partnerUiSettings]
  );

  const value = useMemo<PartnerUiContextType>(
    () => ({
      whiteLabelEnabled,
      partnerDetails,
      logoUrl: partnerUiSettings?.logo_url,
      partnerCode: partnerUiSettings?.partner_code,
      partnerId: partnerUiSettings?.partner_id,
    }),
    [whiteLabelEnabled, partnerDetails, partnerUiSettings]
  );

  if (whiteLabelEnabled && (!partnerDomain || (isLoading && !isFetched))) {
    return <LoadingCenter color={Colors.bw.black} />;
  }

  return <PartnerUiContext.Provider value={value}>{children}</PartnerUiContext.Provider>;
};
